import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  trackOnboardingError,
  trackOnboardingEvent,
} from "../analytics/onboarding";
import {
  trackRitualBathsError,
  trackRitualBathsEvent,
} from "../analytics/ritualBaths";
import type { FormChatState } from "../repositories/formChat.repository";
import {
  ritualBathQueryKeys,
  ritualBathRepository,
} from "../repositories/ritualBath.repository";
import {
  buildRitualBathCreatePayload,
  buildRitualBathUpdatePayload,
} from "../utils/ritualBathFormChatPayloadBuilder";
import { notifyLimitReached } from "../utils/limitToasts";
import { toast } from "../utils/toast";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useFeatureLimit } from "./useFeatureLimit";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import {
  useCreateRitualBathMutation,
  useUpdateRitualBathMutation,
} from "./useRitualBathMutations";

const SOURCE_PAGE = "FormChatAssistant";
const INCOMPLETE_MESSAGE = "Revise os dados do banho no chat antes de confirmar.";

export class RitualBathChatSubmitError extends Error {}

const readLimitMessage = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 402) return null;
  const data = error.response.data as { error?: unknown; message?: unknown } | undefined;
  const message = data?.error ?? data?.message;
  return typeof message === "string" && message ? message : "";
};

export function useFormChatSubmitRitualBath({
  editTargetId,
  enabled = true,
}: {
  editTargetId?: string;
  enabled?: boolean;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const productBase = useProductAnalyticsBase();
  const terreiroId = useCachedTerreiro()?.id;
  const mediumId = useCachedMedium()?.id;
  const isEdit = Boolean(editTargetId);
  const limit = useFeatureLimit({
    feature: "maxRitualBaths",
    enabled: enabled && !isEdit,
  });
  const createMutation = useCreateRitualBathMutation();
  const updateMutation = useUpdateRitualBathMutation();
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  /** The bath is already saved, so a failed photo upload only warns instead of failing the submit. */
  const uploadImage = useCallback(async (bathId: string, image: File | null) => {
    if (!image) return;
    setIsUploadingImage(true);
    try {
      await ritualBathRepository.uploadRitualBathImage(bathId, image);
      await qc.invalidateQueries({ queryKey: ritualBathQueryKeys.all });
    } catch {
      toast.error("O banho foi salvo, mas não foi possível enviar a foto.");
    } finally {
      setIsUploadingImage(false);
    }
  }, [qc]);

  const notifyIfLimit = useCallback(
    (error: unknown) => {
      const limitMessage = readLimitMessage(error);
      if (limitMessage === null) return;
      notifyLimitReached({
        feature: "maxRitualBaths",
        message: limitMessage || limit.message,
      });
    },
    [limit.message],
  );

  const submitRitualBathByChat = useCallback(
    async ({
      state,
      dirtyFields = [],
      image = null,
    }: {
      state: FormChatState;
      dirtyFields?: string[];
      image?: File | null;
    }) => {
      const analyticsBase = { ...productBase, trigger: "mutation" as const };
      const eventSource = { source: "form_chat" };

      if (isEdit && editTargetId) {
        const payload = buildRitualBathUpdatePayload(state, dirtyFields);
        if (!payload && !image) {
          throw new RitualBathChatSubmitError("Nenhuma alteração para salvar.");
        }
        const eventProps = { ritual_bath_id: editTargetId, ...eventSource };
        trackRitualBathsEvent(
          "ritual_bath_update_submit_attempt",
          analyticsBase,
          eventProps,
        );
        if (payload) {
          try {
            await updateMutation.mutateAsync({ id: editTargetId, payload });
          } catch (error) {
            trackRitualBathsError(
              "ritual_bath_update_error",
              analyticsBase,
              error,
              eventProps,
            );
            throw error;
          }
        }
        await uploadImage(editTargetId, image);
        trackRitualBathsEvent("ritual_bath_updated", analyticsBase, eventProps);
        toast.success("Banho atualizado.");
        navigate(`/ritual-baths/${editTargetId}`);
        return;
      }

      const onboardingBase = {
        sourcePage: SOURCE_PAGE,
        terreiroId,
        mediumId,
        trigger: "mutation" as const,
      };
      const payload = buildRitualBathCreatePayload(state, { terreiroId, mediumId });
      if (!payload) throw new RitualBathChatSubmitError(INCOMPLETE_MESSAGE);
      const scopeProps = {
        origin: payload.origin,
        category_id_present: Boolean(payload.categoryId),
        ...eventSource,
      };

      if (!limit.canCreate) {
        trackOnboardingEvent(
          "onboarding_ritual_bath_created",
          { ...onboardingBase, success: false, featureLimitBlocked: true },
          scopeProps,
        );
        trackRitualBathsEvent(
          "ritual_bath_create_submit_attempt",
          { ...analyticsBase, success: false, featureLimitBlocked: true },
          scopeProps,
        );
        notifyLimitReached({ feature: "maxRitualBaths", message: limit.message });
        throw new RitualBathChatSubmitError(
          limit.message || "Limite de banhos atingido.",
        );
      }

      trackRitualBathsEvent(
        "ritual_bath_create_submit_attempt",
        analyticsBase,
        scopeProps,
      );
      let createdId: string;
      try {
        createdId = (await createMutation.mutateAsync(payload)).id;
      } catch (error) {
        notifyIfLimit(error);
        trackOnboardingError(
          "onboarding_ritual_bath_created",
          onboardingBase,
          error,
          scopeProps,
        );
        trackRitualBathsError(
          "ritual_bath_create_error",
          analyticsBase,
          error,
          scopeProps,
        );
        throw error;
      }
      trackOnboardingEvent("onboarding_ritual_bath_created", onboardingBase, scopeProps);
      trackRitualBathsEvent("ritual_bath_created", analyticsBase, scopeProps);
      await uploadImage(createdId, image);
      await limit.invalidate();
      toast.success("Banho criado.");
      navigate(`/ritual-baths/${createdId}`);
    },
    [
      createMutation,
      editTargetId,
      isEdit,
      limit,
      mediumId,
      navigate,
      notifyIfLimit,
      productBase,
      terreiroId,
      updateMutation,
      uploadImage,
    ],
  );

  return {
    submitRitualBathByChat,
    canCreate: isEdit || limit.canCreate,
    isLimitLoading: !isEdit && limit.isLoading,
    limitMessage: limit.message,
    isSubmitting:
      createMutation.isPending || updateMutation.isPending || isUploadingImage,
  };
}
